import { atom } from 'nanostores';
import { type ActionUpdate, type BoltAction, type DeployAction } from './actions';
import type { ActionCallbackData } from './message-parser';
import { withResolvers } from '~/utils/promises';
import { unreachable } from '~/utils/unreachable';

type DeployStatus = Awaited<ReturnType<DeployAction['run']>> | undefined;

export class ActionsRunner {
  #currentExecutionPromise: Promise<void> = Promise.resolve();
  #deployStatus: PromiseWithResolvers<DeployStatus> | undefined = undefined;

  stateUpdates = atom(0);
  actions = new Map<string, BoltAction>();

  getDeployStatus() {
    return this.#deployStatus?.promise;
  }

  addAction(data: ActionCallbackData, shouldRun: boolean) {
    const actionId = data.action.id;
    const action = this.actions.get(actionId);

    if (action) {
      // action already added
      return;
    }

    this.actions.set(actionId, data.action);

    if (!shouldRun) {
      this.#updateAction(actionId, { status: 'complete', executed: true });
    }

    this.actions.set(actionId, data.action);

    this.#notify();

    // a deploy is done by piping `build` to `deploy`
    if (data.action.type === 'build-and-deploy') {
      this.#deployStatus = withResolvers<DeployStatus>();
    }

    if (shouldRun) {
      this.#currentExecutionPromise.then(() => {
        this.#updateAction(actionId, { status: 'running' });
      });
    }
  }

  async runAction(data: ActionCallbackData) {
    const actionId = data.action.id;
    const action = this.actions.get(actionId);

    if (!action) {
      unreachable(`Action ${actionId} not found`);
    }

    if (action.executed) {
      return this.#currentExecutionPromise;
    }

    this.#updateAction(actionId, { content: data.action.content.get(), executed: true });

    this.#currentExecutionPromise = this.#currentExecutionPromise
      .then(() => {
        return this.#executeAction(actionId);
      })
      .catch((error) => {
        console.error(`Action failed\n\n${error}`);

        throw error;
      });

    return this.#currentExecutionPromise;
  }

  async #executeAction(actionId: string) {
    const action = this.actions.get(actionId);

    if (!action) {
      unreachable(`Action ${actionId} not found`);
    }

    if (action.abortSignal.aborted) {
      return;
    }

    // a deploy is done by piping `build` to `deploy`
    const isDeployAction = action.type === 'build-and-deploy';
    const isShellAction = action.type === 'shell';

    this.#updateAction(actionId, { status: 'running' });

    try {
      const result = await action.run();

      if (isDeployAction) {
        this.#deployStatus?.resolve(result as DeployStatus);
      }

      // cancel all command running actions when a shell action fails
      if (isShellAction && result !== 0) {
        for (const pendingAction of this.actions.values()) {
          if (
            pendingAction.status.value === 'pending' &&
            (pendingAction.type === 'shell' || pendingAction.type === 'start')
          ) {
            pendingAction.abort();
          }
        }
      }

      if (action.abortSignal.aborted) {
        const status = action.abortSignal.reason;
        this.#updateAction(actionId, { status });
      } else if (action.status.value === 'running') {
        this.#updateAction(actionId, { status: 'complete' });
      } else {
        this.#notify();
      }
    } catch (error) {
      if (isDeployAction) {
        this.#deployStatus?.reject(error);
      }

      let errorMessage: string | undefined;

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      this.#updateAction(actionId, { status: 'failed', ...(errorMessage && { error: errorMessage }) });

      // re-throw the error to be caught in the promise chain
      throw error;
    }
  }

  abort() {
    for (const action of Object.values(this.actions)) {
      if (action.status.value === 'running') {
        action.abort();
      }
    }
  }

  #updateAction(id: string, newState: ActionUpdate) {
    const action = this.actions.get(id);

    if (!action) {
      unreachable(`Action ${id} not found`);
    }

    action.update(newState);

    this.#notify();
  }

  #notify() {
    this.stateUpdates.set(this.stateUpdates.get() + 1);
  }

  listen(cb: VoidFunction) {
    const disposers = new Set<VoidFunction>();

    disposers.add(this.stateUpdates.listen(cb));

    for (const action of this.actions.values()) {
      disposers.add(action.listen(cb));
    }

    return () => {
      disposers.forEach((dispose) => dispose());
    };
  }
}
